import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CurrencyMaskConfig } from '@ngodings/ngx-currency';
import { SearchOptions } from '@components/search-input/search-input.component';
import { SelectOptions } from '@components/select-input/select-input.component';
import { DateOptions } from '@components/date-input/date-input.component';
import { DateRangeOptions } from '@components/date-range-input/date-range-input.component';
import { TableOptions } from '@components/table/table.component';
import { ButtonOptions } from '@components/button/button.component';
import { PaginatorOptions } from '@components/paginator/paginator.component';

export type InputType =
  | 'text'
  | 'money'
  | 'time'
  | 'textarea'
  | 'select'
  | 'search'
  | 'date'
  | 'date-range'
  | 'checkbox'
  | 'table'
  | 'table-with-footer'
  | 'paginator'
  | 'button'
  | 'separator'
  | 'telefone';

export interface Campo {
  name: string;
  label: string;
  value?: any;
  type: InputType;
  icon?: string;
  mask?: string;
  disabled?: boolean;
  hide?: boolean;
  width?: string;
  selectOptions?: SelectOptions;
  searchOptions?: SearchOptions;
  dateOptions?: DateOptions;
  dateRangeOptions?: DateRangeOptions;
  tableOptions?: TableOptions;
  paginatorOptions?: PaginatorOptions;
  buttonOptions?: ButtonOptions;
  moneyOptions?: Partial<CurrencyMaskConfig>;
  validators?: any[];
  onChange?: (value: any) => void;
  tooltip?: string;
}

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
})
export class FormComponent {
  @Input()
  appearance: 'fill' | 'outline' = 'outline';

  @Input()
  campos: Campo[] = [];

  @Output()
  onSubmit = new EventEmitter<any>();

  public form = new FormGroup({});

  get camposVisiveis() {
    return this.campos.filter((campo) => !campo.hide);
  }

  /**
   * Método para obter o status de validação do formulário
   */
  get isValid() {
    return this.form.valid;
  }

  /**
   * Método para obter o status de validação do formulário
   * retornando true apenas se todos os campos estão habilitados
   */
  get isInvalid() {
    return this.form.invalid;
  }

  get isEmpty() {
    return !Object.entries(this.form.value).some(([_, value]) => value);
  }

  /**
   * Método para obter os valores do formulário respeitando a profundidade de objetos
   * passados no atributo name dos campos.
   *
   * Ex: campo.name = 'veiculo.placa' -> { veiculo: { placa: 'AAA-0000' } }
   **/
  get values() {
    return Object.entries(this.form.getRawValue()).reduce((acc: any, [key, value]) => {
      const keys = key.split('.');
      const lastKey = keys.pop() as string;
      let obj = acc;

      keys.forEach((key) => {
        const match = key.match(/(.*)\[(\d+)\]/);

        if (match) {
          const key = match[1];
          const index = match[2];

          obj[key] = obj[key] || [];
          obj[key][index] = obj[key][index] || {};
          obj = obj[key][index];
        } else {
          obj[key] = obj[key] || {};
          obj = obj[key];
        }
      });

      obj[lastKey] = value;

      return acc;
    }, {});
  }

  constructor(private formBuilder: FormBuilder) {}

  /**
   * Método para obter o valor de um campo do formulário
   *
   * @param name nome do campo do formulário
   * @returns valor do campo do formulário
   */
  public get(name: string) {
    return this.form.get([name])?.value;
  }

  /**
   * Método para carregar os valores de campos do formulário
   * respeitando a profundidade de objetos.
   *
   * Ex: campo.name = 'veiculo.placa' e data = { veiculo: { placa: 'AAA-0000' } }
   *
   * Também é possível carregar campos de arrays, porém, o nome do campo deve
   * ser informado com o índice do array.
   *
   * Ex: campo.name = 'veiculos[0].placa' e data = { veiculos: [{ placa: 'AAA-0000' }] }
   **/
  public load(data: any) {
    this.campos.forEach((campo) => {
      if (!campo.name) return;

      const value = campo.name.split('.').reduce((acc, curr) => {
        if (!acc) return null;

        const index = curr.match(/(.*)\[(\d+)\]/);

        if (index) {
          return acc[index[1]][index[2]];
        }

        return acc[curr];
      }, data);

      if (value !== null) {
        this.form.get([campo.name])?.setValue(value);
      }
    });
  }

  /**
   * Método para resetar o formulário
   **/
  public reset() {
    this.form.reset();
  }

  protected ngOnChanges(changes: SimpleChanges) {
    if (changes['campos']) {
      this.form = this.buildForm();
    }
  }

  private buildForm() {
    const formGroup: {
      [key: string]: any;
    } = {};

    this.campos.forEach((campo) => {
      if (!campo.name) return;

      /**
       * Caso não for passado um valor para o campo, o valor padrão deve ser null
       * para ser possível altera-lo posteriormente.
       */
      formGroup[campo.name] = [
        { value: campo.value ?? null, disabled: campo.disabled },
        campo.validators,
      ];
    });

    return this.formBuilder.group(formGroup);
  }

  public patchValue(data: any) {
    this.form.patchValue(data);
  }

  /**
   * Método para submeter o formulário
   *
   * Semelhanete com a entrada de dados do formulário, o valor de saída
   * é um objeto com a profundidade de objetos.
   **/
  protected submit() {
    this.onSubmit.emit(this.form.value);
  }
}
